import React from "react";
import {useIntl} from "react-intl";
import Skeleton from "react-loading-skeleton";
import {
  IconCheck,
  IconDeviceTv,
  IconDeviceTvOff,
  IconDisabled,
  IconDisabledOff,
  IconFence,
  IconFenceOff,
  IconFlame,
  IconFlameOff,
  IconParking,
  IconParkingOff,
  IconPaw,
  IconPool,
  IconSmoking,
  IconSmokingNo,
  IconWashMachine,
  IconWifi,
  IconWifiOff, IconX
} from "@tabler/icons-react";
import {AnyPrice, classNames, TranslatedString, useAppConfig} from "@ct-react/core";
import {useLocaleFormatter} from "@ct-react/locale";
import {SiteType} from "@shared/site-type";
import {CustomViewType} from "@shared/models/app-context";
import {featureTranslations} from "../../../i18n/sharable-defs";
import {SkeletonableProps} from "../../../tools/components";
import SaunaSvg from "../../../assets/componable-svgs/feat-sauna.svg";
import HotTubSvg from "../../../assets/componable-svgs/feat-hottub.svg";
import HammamSvg from "../../../assets/componable-svgs/feat-hammam.svg";
import DishwasherSvg from "../../../assets/componable-svgs/feat-dishwasher.svg";
import BalconySvg from "../../../assets/componable-svgs/feat-balcony.svg";
import SmokeAlarmSvg from "../../../assets/componable-svgs/feat-smoke-alarm.svg";
import CarbonAlarmSvg from "../../../assets/componable-svgs/feat-carbon-alarm.svg";
import SmokeAlarmOffSvg from "../../../assets/componable-svgs/feat-smoke-alarm-off.svg";
import CarbonAlarmOffSvg from "../../../assets/componable-svgs/feat-carbon-alarm-off.svg";
import "./full-features.scss"

interface FeaturesData {
  isPetCompliant: boolean;
  isDisabledPersonCompliant: boolean;
  withBalcony: boolean;
  withTerrace: boolean;
  withParking: boolean;
  withWIFI: boolean;
  withTV: boolean;
  withLift: boolean;
  withDishwasher: boolean;
  withWashingMachine: boolean;
  withLaundryRoom: boolean;
  withSauna: boolean;
  withHotTub: boolean;
  withHammam: boolean;
  withPool: boolean;
  withFireplace: boolean;
  isSmokerCompliant: boolean;
  withSmokeAlarm: boolean;
  withCarbonAlarm: boolean;
  livingSpace: string;
  orientation: string;
  isSellableToForeigners: boolean;
  canBeSecondaryHome: boolean;
  constructionYear: string;
  floor: TranslatedString;
  isNew: boolean;
  isFurnished: boolean;
  rooms: number;
  bedrooms: number;
  bathrooms: number;
  charges: AnyPrice;
  renovationFund: AnyPrice;
}


type FullFeaturesProps = SkeletonableProps<FeaturesData>;

const DisplayFullFeatures = (content: FeaturesData) => {

  const intl = useIntl();
  const {options: {customView, siteType}} = useAppConfig();
  const {print, transLib} = useLocaleFormatter();

  const toClasses = (feature: boolean, nonRental?: boolean) => classNames("a-feature", {
    "yes": feature,
    "no": !feature,
    "mask": (!feature && customView === CustomViewType.HOTEL) || (!feature && (nonRental ?? false))
  })

  return (
    <>
      {siteType === SiteType.HOMEALP &&
        <div className="features-list boxed">
            <span
              className={toClasses(content!.isSellableToForeigners, true)}>{intl.formatMessage(featureTranslations.isSellableToForeigners)}:&nbsp;{content!.isSellableToForeigners ?
              <IconCheck/> : <IconX/>} </span>
          <span
            className={toClasses(content!.canBeSecondaryHome, true)}>{intl.formatMessage(featureTranslations.canBeSecondaryHome)}:&nbsp;{content!.canBeSecondaryHome ?
            <IconCheck/> : <IconX/>} </span>
          <span
            className={toClasses(content!.isNew, true)}>{intl.formatMessage(featureTranslations.isNew)}:&nbsp;{content!.isNew ?
            <IconCheck/> :
            <IconX/>} </span>
          <span
            className={toClasses(content!.isFurnished, true)}>{intl.formatMessage(featureTranslations.isFurnished)}:&nbsp;{content!.isFurnished ?
            <IconCheck/> : <IconX/>} </span>
          <span
            className={toClasses(!!content!.livingSpace, true)}>{intl.formatMessage(featureTranslations.livingSpace)}:&nbsp;{content!.livingSpace} m²</span>
          <span
            className={toClasses(!!content!.orientation, true)}>{intl.formatMessage(featureTranslations.orientation)}:&nbsp;{transLib.match("orientations", content.orientation)} </span>
          <span
            className={toClasses(!!content!.constructionYear, true)}>{intl.formatMessage(featureTranslations.constructionYear)}:&nbsp;{content!.constructionYear} </span>
          <span
            className={toClasses(!!content!.floor, true)}>{intl.formatMessage(featureTranslations.floor)}:&nbsp;{content!.floor?.value} </span>
          <span
            className={toClasses(!!content!.rooms, true)}>{intl.formatMessage(featureTranslations.rooms, {roomCount: content!.rooms})} </span>
          <span
            className={toClasses(!!content!.bedrooms, true)}>{intl.formatMessage(featureTranslations.bedrooms, {bedCount: content!.bedrooms})} </span>
          <span
            className={toClasses(!!content!.bathrooms, true)}>{intl.formatMessage(featureTranslations.bathrooms, {bathCount: content!.bathrooms})}  </span>
          <span
            className={toClasses(!!content!.charges, true)}>{intl.formatMessage(featureTranslations.charges)}:&nbsp;{content!.charges ? print.price(content!.charges, {trailingZeroDisplay: "stripIfInteger"}) : '/'} </span>
          <span
            className={toClasses(!!content!.renovationFund, true)}>{intl.formatMessage(featureTranslations.renovationFund)}:&nbsp;{content!.renovationFund ? print.price(content!.renovationFund, {trailingZeroDisplay: "stripIfInteger"}) : '/'} </span>
        </div>
      }

      <div className="features-list">

        {siteType === SiteType.RENTALP &&
          <>
            <span className={toClasses(content!.isPetCompliant)}>
              <IconPaw/>
              {intl.formatMessage(featureTranslations.pet)}
            </span>
            <span className={toClasses(content!.isDisabledPersonCompliant)}>
              {content!.isDisabledPersonCompliant ? <IconDisabled/> : <IconDisabledOff/>}
              {intl.formatMessage(featureTranslations.disabledPerson)}
            </span>
          </>
        }
        <span className={toClasses(content!.withBalcony)}>
          <BalconySvg className="custom"/>
          {intl.formatMessage(featureTranslations.balcony)}
        </span>
        <span className={toClasses(content!.withTerrace)}>
          {content!.withTerrace ? <IconFence/> : <IconFenceOff/>}
          {intl.formatMessage(featureTranslations.terrace)}
        </span>
        <span className={toClasses(content!.withParking)}>
          {content!.withParking ? <IconParking/> : <IconParkingOff/>}
          {intl.formatMessage(featureTranslations.parking)}
        </span>
        {siteType === SiteType.RENTALP &&
          <span className={toClasses(content!.withWIFI)}>
            {content!.withWIFI ? <IconWifi/> : <IconWifiOff/>}
            {intl.formatMessage(featureTranslations.wifi)}
          </span>
        }
        <span className={toClasses(content!.withTV)}>
          {content!.withTV ? <IconDeviceTv/> : <IconDeviceTvOff/>}
          {intl.formatMessage(featureTranslations.tv)}
        </span>
        <span className={toClasses(content!.withDishwasher)}>
          <DishwasherSvg className="custom"/>
          {intl.formatMessage(featureTranslations.dishwasher)}
        </span>
        <span className={toClasses(content!.withWashingMachine || content!.withLaundryRoom)}>
          <IconWashMachine/>
          {intl.formatMessage(content.withLaundryRoom && !content.withWashingMachine ? featureTranslations.laundryRoom : featureTranslations.washingMachine)}
        </span>
        <span className={toClasses(content!.withPool)}>
          <IconPool/>
          {intl.formatMessage(featureTranslations.pool)}
        </span>
        <span className={toClasses(content!.withSauna)}>
          <SaunaSvg className="custom"/>
          {intl.formatMessage(featureTranslations.sauna)}
        </span>
        {siteType === SiteType.RENTALP &&
          <>
            <span className={toClasses(content!.withHotTub)}>
              <HotTubSvg className="custom" />
              {intl.formatMessage(featureTranslations.hottub)}
            </span>
            <span className={toClasses(content!.withHammam)}>
              <HammamSvg className="custom" />
              {intl.formatMessage(featureTranslations.hammam)}
            </span>
          </>
        }
        <span className={toClasses(content!.withFireplace)}>
          {content!.withFireplace ? <IconFlame/> : <IconFlameOff/>}
          {intl.formatMessage(featureTranslations.fireplace)}
        </span>
        {siteType === SiteType.RENTALP &&
          <>
            <span className={toClasses(!content!.isSmokerCompliant)}>
              {content!.isSmokerCompliant ? <IconSmoking/> : <IconSmokingNo/>}
              {intl.formatMessage(featureTranslations.smoker)}
            </span>
            <span className={toClasses(content!.withSmokeAlarm)}>
              {content!.withSmokeAlarm ? <SmokeAlarmSvg className="custom"/> : <SmokeAlarmOffSvg className="custom"/>}
              {intl.formatMessage(featureTranslations.smokerAlarm)}
            </span>
            <span className={toClasses(content!.withCarbonAlarm)}>
              {content!.withCarbonAlarm ? <CarbonAlarmSvg className="custom"/> : <CarbonAlarmOffSvg className="custom"/>}
              {intl.formatMessage(featureTranslations.carbonAlarm)}
          </span>
          </>
        }
      </div>
    </>);

}

const FullFeatures = (
  {
    loading,
    className,
    data,
  }: FullFeaturesProps) => {

  const intl = useIntl();

  return (
    <div className={classNames("rla-features", className)}>
      <h2>
        {!loading
          ? intl.formatMessage({
            id: "booking-features-title",
            defaultMessage: "Ce que propose ce logement"
          })
          : <Skeleton inline={true} width="50%"/>
        }
      </h2>
      {!loading
        ?
        <DisplayFullFeatures {...data!} />

        : <Skeleton containerClassName="features-list" className="a-feature" count={15} inline={true}/>
      }
    </div>);

}

export default FullFeatures;

